/**
 * PR-6 — Prompt-ops admin dashboard.
 *
 * Four tabs over /api/admin/prompt-ops:
 *   - Registry: per-stage active prompt version + hash, diff vs prior,
 *     and the human-gated promote / rollback controls.
 *   - Eval volume: trainable signal per stage (verdict + attribution conf).
 *   - Attribution: the officer "which step failed" triage queue — resolve
 *     ambiguous eval examples by attributing them to a real stage.
 *   - MIPRO: run history, empty-stated until PR-2 lands its run table.
 */

import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../lib/api'
import {
  attributeExample,
  fetchEvalExamples,
  fetchEvalStats,
  fetchMiproRuns,
  fetchRegistry,
  fetchServingMap,
  fetchStages,
  promoteVersion,
  rollbackVersion,
  type DiffLine,
  type EvalExample,
  type EvalStats,
  type MiproRuns,
  type RegistryOverview,
  type RegistryStageEntry,
  type ServingMap,
} from '../lib/promptOps'

type Tab = 'serving' | 'registry' | 'eval' | 'attribution' | 'mipro'

const cardStyle = {
  borderRadius: 'var(--radius-card)',
  boxShadow: 'var(--shadow-card)',
}

function shortHash(h: string): string {
  return h.slice(0, 12)
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: string } | undefined
    return body?.detail || `${e.status} ${e.statusText}` || fallback
  }
  return fallback
}

// ── Diff viewer ───────────────────────────────────────────────────────────────
function DiffView({ lines }: { lines: DiffLine[] }) {
  if (!lines.length) {
    return <div className="text-sm text-slate-500">No prior version — this is the baseline.</div>
  }
  const color = (k: DiffLine['kind']) =>
    k === 'add'
      ? { background: 'rgba(15,117,99,.10)', color: 'var(--brand-verified)' }
      : k === 'del'
        ? { background: 'rgba(179,64,30,.10)', color: '#9c3a1c' }
        : k === 'meta'
          ? { color: '#64748b', background: 'rgba(100,116,139,.06)' }
          : { color: '#334155' }
  return (
    <pre
      className="text-xs overflow-auto p-2 rounded"
      style={{ fontFamily: 'var(--font-mono, monospace)', maxHeight: 320, background: '#fff' }}
    >
      {lines.map((l, i) => (
        <div key={i} style={{ ...color(l.kind), padding: '0 4px', whiteSpace: 'pre-wrap' }}>
          {l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : l.kind === 'meta' ? '' : '  '}
          {l.text}
        </div>
      ))}
    </pre>
  )
}

// ── Serving map tab ───────────────────────────────────────────────────────────
function ServingTab() {
  const [data, setData] = useState<ServingMap | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchServingMap()
      .then((d) => {
        setData(d)
        setError(null)
      })
      .catch((e) => setError(errMsg(e, 'Failed to load serving map')))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="text-sm text-slate-500">Loading serving map…</div>
  if (error)
    return (
      <div className="text-sm" style={{ color: 'var(--brand-gap)' }}>
        {error}
      </div>
    )
  if (!data) return null

  const anyPromoted = data.rows.some((r) => r.serving === 'promoted')
  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        Every live LLM call site: which prompt text production serves right now, and how much it
        runs.{' '}
        {anyPromoted
          ? 'Highlighted rows serve a promoted version instead of the code constant.'
          : 'All call sites currently serve their code constants — no promotion is live.'}
      </p>
      <div className="bg-white p-4 overflow-x-auto" style={cardStyle}>
        <table className="text-sm w-full">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-3">Agent</th>
              <th className="py-1.5 pr-3">Serving</th>
              <th className="py-1.5 pr-3">Active v</th>
              <th className="py-1.5 pr-3 text-right">Calls 24h</th>
              <th className="py-1.5 pr-3 text-right">Calls 7d</th>
              <th className="py-1.5 pr-3 text-right">Tokens 7d</th>
              <th className="py-1.5 pr-3">Last call</th>
              <th className="py-1.5">Prompt ids</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr
                key={r.agent_name}
                className="border-b border-slate-100"
                style={r.serving === 'promoted' ? { background: 'rgba(37,99,235,.06)' } : undefined}
              >
                <td className="py-1.5 pr-3 font-medium text-slate-900">{r.agent_name}</td>
                <td className="py-1.5 pr-3">
                  {r.serving === 'promoted' ? (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded text-white"
                      style={{ background: 'var(--brand-blue, #2563eb)' }}
                    >
                      promoted
                    </span>
                  ) : (
                    <span className="text-xs text-slate-500">code</span>
                  )}
                </td>
                <td className="py-1.5 pr-3">{r.active_version ?? '—'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.calls_24h}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.calls_7d}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">
                  {r.tokens_7d.toLocaleString()}
                </td>
                <td className="py-1.5 pr-3 text-xs text-slate-500">
                  {r.last_call_at ? new Date(r.last_call_at).toLocaleString() : 'never'}
                </td>
                <td className="py-1.5 text-xs text-slate-500">{r.prompt_ids.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 mt-3">
        Officer labels collected per stage:{' '}
        {Object.keys(data.eval_examples_by_stage).length
          ? Object.entries(data.eval_examples_by_stage)
              .sort()
              .map(([stage, n]) => `${stage} ${n}`)
              .join(' · ')
          : 'none yet'}
      </div>
    </div>
  )
}

// ── Registry tab ──────────────────────────────────────────────────────────────
function RegistryRow({ entry, onChanged }: { entry: RegistryStageEntry; onChanged: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const active = entry.active

  const doPromote = async (version: number) => {
    setBusy(true)
    setMsg(null)
    try {
      await promoteVersion(entry.agent_name, version)
      onChanged()
    } catch (e) {
      setMsg(errMsg(e, 'Promote failed'))
    } finally {
      setBusy(false)
    }
  }

  const doRollback = async () => {
    if (!window.confirm(`Roll back ${entry.agent_name} to the previous version?`)) return
    setBusy(true)
    setMsg(null)
    try {
      await rollbackVersion(entry.agent_name)
      onChanged()
    } catch (e) {
      setMsg(errMsg(e, 'Rollback failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-white p-4 mb-3" style={cardStyle}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900">{entry.agent_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {active ? (
              <>
                active <strong>v{active.version}</strong> · {shortHash(active.prompt_hash)} ·{' '}
                {active.prompt_chars} chars
                {active.activated_at ? ' · promoted by human' : ''}
              </>
            ) : (
              <span style={{ color: 'var(--brand-gap)' }}>no active version</span>
            )}
            {' · '}
            {entry.total_versions} version{entry.total_versions === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {entry.diff && (entry.diff.added > 0 || entry.diff.removed > 0) ? (
            <span className="text-xs" title="changes vs prior version">
              <span style={{ color: 'var(--brand-verified)' }}>+{entry.diff.added}</span>{' '}
              <span style={{ color: '#9c3a1c' }}>−{entry.diff.removed}</span>
            </span>
          ) : null}
          <button
            className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Hide' : 'Diff & versions'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="text-xs text-slate-500 mb-1">
            Diff {entry.prior ? `v${entry.prior.version} → ` : '(baseline) → '}v
            {active?.version ?? entry.diff?.to_version}
          </div>
          {entry.diff ? <DiffView lines={entry.diff.lines} /> : <div className="text-sm">—</div>}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <PromoteControl
              max={entry.total_versions - 1}
              activeVersion={active?.version ?? null}
              disabled={busy}
              onPromote={doPromote}
            />
            <button
              className="text-xs px-2 py-1 rounded text-white disabled:opacity-50"
              style={{ background: 'var(--brand-navy, #1f2a44)' }}
              disabled={busy || !active || active.version === 0}
              onClick={doRollback}
            >
              Roll back
            </button>
            {msg && (
              <span className="text-xs" style={{ color: 'var(--brand-gap)' }}>
                {msg}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 mt-2">
            Promotion marks the chosen version as active (human-gated, never automatic). Runtime
            consumption of the active prompt lands with PR-2.
          </div>
        </div>
      )}
    </div>
  )
}

function PromoteControl({
  max,
  activeVersion,
  disabled,
  onPromote,
}: {
  max: number
  activeVersion: number | null
  disabled: boolean
  onPromote: (v: number) => void
}) {
  const [v, setV] = useState('')
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        max={Math.max(0, max)}
        placeholder="version"
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="text-xs w-20 px-2 py-1 rounded border border-slate-300"
      />
      <button
        className="text-xs px-2 py-1 rounded text-white disabled:opacity-50"
        style={{ background: 'var(--brand-blue, #2563eb)' }}
        disabled={disabled || v === '' || Number(v) === activeVersion}
        onClick={() => onPromote(Number(v))}
      >
        Promote
      </button>
    </div>
  )
}

function RegistryTab() {
  const [data, setData] = useState<RegistryOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchRegistry())
      setError(null)
    } catch (e) {
      setError(errMsg(e, 'Failed to load registry'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) return <div className="text-sm text-slate-500">Loading registry…</div>
  if (error)
    return (
      <div className="text-sm" style={{ color: 'var(--brand-gap)' }}>
        {error}
      </div>
    )
  if (!data) return null

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        {data.agents.length} registered prompt{data.agents.length === 1 ? '' : 's'}. Each is the
        version-defining system prompt of a pipeline stage.
      </p>
      {data.agents.map((a) => (
        <RegistryRow key={a.agent_name} entry={a} onChanged={load} />
      ))}
    </div>
  )
}

// ── Eval volume tab ───────────────────────────────────────────────────────────
function EvalTab() {
  const [stats, setStats] = useState<EvalStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchEvalStats()
      .then(setStats)
      .catch((e) => setError(errMsg(e, 'Failed to load eval stats')))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="text-sm text-slate-500">Loading eval volume…</div>
  if (error)
    return (
      <div className="text-sm" style={{ color: 'var(--brand-gap)' }}>
        {error}
      </div>
    )
  if (!stats) return null

  return (
    <div>
      <div className="flex gap-4 mb-4 text-sm">
        <div className="bg-white px-4 py-3" style={cardStyle}>
          <div className="text-2xl font-semibold text-slate-900">{stats.total_examples}</div>
          <div className="text-xs text-slate-500">total examples</div>
        </div>
        <div className="bg-white px-4 py-3" style={cardStyle}>
          <div className="text-2xl font-semibold" style={{ color: 'var(--brand-gap)' }}>
            {stats.ambiguous_unresolved}
          </div>
          <div className="text-xs text-slate-500">ambiguous (need attribution)</div>
        </div>
      </div>

      {stats.by_stage.length === 0 ? (
        <div className="text-sm text-slate-500">
          No eval examples collected yet. They accrue as officers confirm/edit/reject proposals.
        </div>
      ) : (
        <table className="w-full text-sm bg-white" style={cardStyle}>
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="p-2">Stage</th>
              <th className="p-2 text-right">Positive</th>
              <th className="p-2 text-right">Negative</th>
              <th className="p-2 text-right">Clean</th>
              <th className="p-2 text-right">Ambiguous</th>
              <th className="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {stats.by_stage.map((s) => (
              <tr key={s.stage} className="border-b border-slate-50">
                <td className="p-2 font-medium text-slate-800">{s.stage}</td>
                <td className="p-2 text-right" style={{ color: 'var(--brand-verified)' }}>
                  {s.positive}
                </td>
                <td className="p-2 text-right" style={{ color: '#9c3a1c' }}>
                  {s.negative}
                </td>
                <td className="p-2 text-right">{s.clean}</td>
                <td className="p-2 text-right">{s.ambiguous}</td>
                <td className="p-2 text-right font-semibold">{s.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Attribution triage tab ────────────────────────────────────────────────────
function AttributionRow({
  ex,
  stages,
  onDone,
}: {
  ex: EvalExample
  stages: string[]
  onDone: () => void
}) {
  const [stage, setStage] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const submit = async () => {
    if (!stage) return
    setBusy(true)
    setMsg(null)
    try {
      await attributeExample(ex.id, stage)
      onDone()
    } catch (e) {
      setMsg(errMsg(e, 'Attribution failed'))
      setBusy(false)
    }
  }

  return (
    <tr className="border-b border-slate-50">
      <td className="p-2 text-xs text-slate-600">{ex.object_ref ?? '—'}</td>
      <td className="p-2">
        <span
          className="text-xs px-1.5 py-0.5 rounded"
          style={{
            background:
              ex.verdict === 'negative_explicit' ? 'rgba(179,64,30,.10)' : 'rgba(15,117,99,.10)',
            color: ex.verdict === 'negative_explicit' ? '#9c3a1c' : 'var(--brand-verified)',
          }}
        >
          {ex.verdict === 'negative_explicit' ? 'negative' : 'positive'}
        </span>
      </td>
      <td className="p-2 text-xs text-slate-500">{ex.failure_mode ?? '—'}</td>
      <td className="p-2 text-xs text-slate-500">{ex.split}</td>
      <td className="p-2">
        <div className="flex items-center gap-1">
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className="text-xs px-1 py-1 rounded border border-slate-300"
          >
            <option value="">pick stage…</option>
            {stages.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            className="text-xs px-2 py-1 rounded text-white disabled:opacity-50"
            style={{ background: 'var(--brand-blue, #2563eb)' }}
            disabled={busy || !stage}
            onClick={submit}
          >
            Attribute
          </button>
          {msg && (
            <span className="text-xs" style={{ color: 'var(--brand-gap)' }}>
              {msg}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}

function AttributionTab() {
  const [examples, setExamples] = useState<EvalExample[]>([])
  const [stages, setStages] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [list, st] = await Promise.all([
        fetchEvalExamples({ attribution_confidence: 'ambiguous', limit: 200 }),
        fetchStages(),
      ])
      setExamples(list.examples)
      setStages(st)
      setError(null)
    } catch (e) {
      setError(errMsg(e, 'Failed to load triage queue'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (loading) return <div className="text-sm text-slate-500">Loading triage queue…</div>
  if (error)
    return (
      <div className="text-sm" style={{ color: 'var(--brand-gap)' }}>
        {error}
      </div>
    )

  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">
        Examples PR-1b could not pin to a single stage. Attributing one rewrites its stage and marks
        it cleanly attributed — turning unusable signal into trainable signal.
      </p>
      {examples.length === 0 ? (
        <div className="text-sm text-slate-500">
          No ambiguous examples — nothing to attribute. 🎉
        </div>
      ) : (
        <table className="w-full text-sm bg-white" style={cardStyle}>
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="p-2">Object</th>
              <th className="p-2">Verdict</th>
              <th className="p-2">Signal</th>
              <th className="p-2">Split</th>
              <th className="p-2">Attribute to stage</th>
            </tr>
          </thead>
          <tbody>
            {examples.map((ex) => (
              <AttributionRow key={ex.id} ex={ex} stages={stages} onDone={load} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── MIPRO tab (stub until PR-2) ───────────────────────────────────────────────
function MiproTab() {
  const [data, setData] = useState<MiproRuns | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchMiproRuns()
      .then(setData)
      .catch((e) => setError(errMsg(e, 'Failed to load MIPRO runs')))
  }, [])

  if (error)
    return (
      <div className="text-sm" style={{ color: 'var(--brand-gap)' }}>
        {error}
      </div>
    )
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>

  return (
    <div>
      {data.pending_integration && (
        <div
          className="text-sm p-3 mb-3 rounded"
          style={{ background: 'rgba(37,99,235,.08)', color: '#1e3a8a' }}
        >
          {data.note}
        </div>
      )}
      {data.runs.length === 0 ? (
        <div className="text-sm text-slate-500">
          No MIPRO runs yet. When PR-2 compiles an optimized prompt, its runs appear here with
          baseline vs compiled scores and a one-click promote into the registry.
        </div>
      ) : (
        <table className="w-full text-sm bg-white" style={cardStyle}>
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="p-2">Agent</th>
              <th className="p-2">Status</th>
              <th className="p-2 text-right">Baseline</th>
              <th className="p-2 text-right">Compiled</th>
              <th className="p-2">Completed</th>
            </tr>
          </thead>
          <tbody>
            {data.runs.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="p-2">{r.agent_name}</td>
                <td className="p-2">{r.status}</td>
                <td className="p-2 text-right">{r.baseline_score ?? '—'}</td>
                <td className="p-2 text-right">{r.compiled_score ?? '—'}</td>
                <td className="p-2 text-xs text-slate-500">{r.completed_at ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Page shell ────────────────────────────────────────────────────────────────
const TABS: { key: Tab; label: string }[] = [
  { key: 'serving', label: 'Serving map' },
  { key: 'registry', label: 'Prompt registry' },
  { key: 'eval', label: 'Eval volume' },
  { key: 'attribution', label: 'Attribution' },
  { key: 'mipro', label: 'MIPRO runs' },
]

export default function PromptOpsPage() {
  const [tab, setTab] = useState<Tab>('serving')
  return (
    <div>
      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="px-3 py-2 text-sm"
            style={{
              borderBottom:
                tab === t.key ? '2px solid var(--brand-blue, #2563eb)' : '2px solid transparent',
              color: tab === t.key ? 'var(--brand-blue, #2563eb)' : '#64748b',
              fontWeight: tab === t.key ? 600 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'serving' && <ServingTab />}
      {tab === 'registry' && <RegistryTab />}
      {tab === 'eval' && <EvalTab />}
      {tab === 'attribution' && <AttributionTab />}
      {tab === 'mipro' && <MiproTab />}
    </div>
  )
}
